import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { locationHubService } from '../../services/locationHubService';
import Alert from '../common/Alert';
import { FormLayout, FormField } from '../common/index.js';
import { INDIAN_STATES } from './indianStates';

const AddLocationHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [formData, setFormData] = useState({
    address: '',
    location: '',
    state: '',
    location_code: '',
    destination_type: 'branch_destination',
    location_hub_contact: '',
    commission_percentage: '',
  });

  const filteredStates = INDIAN_STATES.filter((state) =>
    state.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    if (!formData.address.trim()) {
      Alert.error('Address is required');
      return false;
    }
    if (!formData.location.trim()) {
      Alert.error('Location is required');
      return false;
    }
    if (!formData.state) {
      Alert.error('State is required');
      return false;
    }
    if (!formData.location_code.trim()) {
      Alert.error('Location code is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const submitData = {
        address: formData.address.trim(),
        location: formData.location.trim(),
        state: formData.state,
        location_code: formData.location_code.trim().toUpperCase(),
        destination_type: formData.destination_type,
        location_hub_contact: formData.location_hub_contact
          ? parseInt(formData.location_hub_contact)
          : null,
        commission_percentage:
          formData.commission_percentage !== '' ? parseInt(formData.commission_percentage) : 0,
      };

      await locationHubService.createHub(submitData);
      Alert.success('Location hub created successfully!');
      navigate('/location-hubs');
    } catch (error) {
      console.error('Error creating hub:', error);
      Alert.error(error.response?.data?.error || 'Failed to create location hub');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'input-clean w-full text-sm';

  return (
    <FormLayout
      title="Add Destination Hub"
      icon={MapPin}
      backLabel="Back to Destination Hubs"
      onBack={() => navigate('/location-hubs')}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/location-hubs')}
      submitLabel="Create Hub"
      loading={loading}
      maxWidth="max-w-7xl"
    >
      <h3 className="text-base font-semibold text-secondary pb-3 border-b border-gray-100">
        Hub Information
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <FormField label="Address" required>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Enter complete address"
            className={inputClass}
            required
            disabled={loading}
          />
        </FormField>

        <FormField label="Destination" required>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="Enter destination name"
            className={inputClass}
            required
            disabled={loading}
          />
        </FormField>

        <FormField label="Destination Code" required>
          <input
            type="text"
            name="location_code"
            value={formData.location_code}
            onChange={handleChange}
            placeholder="e.g., MU for Mumbai"
            maxLength="10"
            className={`${inputClass} uppercase`}
            required
            disabled={loading}
          />
        </FormField>

        <FormField label="State" required>
          <div className="relative">
            <input
              type="text"
              value={formData.state || stateSearch}
              onChange={(e) => {
                setStateSearch(e.target.value);
                setFormData({ ...formData, state: '' });
                setShowStateDropdown(true);
              }}
              onFocus={() => setShowStateDropdown(true)}
              placeholder="Search and select state"
              className={inputClass}
              required
              disabled={loading}
              autoComplete="off"
            />
            {showStateDropdown && filteredStates.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-card shadow-card max-h-60 overflow-y-auto">
                {filteredStates.map((state) => (
                  <div
                    key={state}
                    onClick={() => {
                      setFormData({ ...formData, state });
                      setStateSearch('');
                      setShowStateDropdown(false);
                    }}
                    className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                  >
                    {state}
                  </div>
                ))}
              </div>
            )}
            {formData.state && (
              <p className="mt-1 text-xs text-gray-500">
                Selected: <span className="font-medium">{formData.state}</span>
              </p>
            )}
          </div>
        </FormField>

        <FormField label="Contact">
          <input
            type="number"
            name="location_hub_contact"
            value={formData.location_hub_contact}
            onChange={handleChange}
            placeholder="Enter contact number"
            className={inputClass}
            disabled={loading}
          />
        </FormField>

        <FormField label="Commission (%)">
          <input
            type="number"
            name="commission_percentage"
            value={formData.commission_percentage}
            onChange={handleChange}
            placeholder="Enter commission %"
            min="0"
            max="100"
            className={inputClass}
            disabled={loading}
          />
        </FormField>

        <FormField label="Destination Type" required>
          <select
            name="destination_type"
            value={formData.destination_type}
            onChange={handleChange}
            className={inputClass}
            disabled={loading}
          >
            <option value="branch_destination">Branch Destination</option>
            <option value="middle_destination">Middle Destination</option>
          </select>
        </FormField>
      </div>
    </FormLayout>
  );
};

export default AddLocationHub;
